// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel'; // Make sure you copied this file into your repo
import type { TeamIdModelType } from './TeamId';
import type { PlanIdModelType } from './PlanId';
import type { OutletsInfoModelType } from './OutletsInfo';
import type { RidersInfoModelType } from './RidersInfo';
import type { PjpSummaryModelType } from './PjpSummary';
import type { PjPlanStatusModelType } from './PjPlanStatus';
import type { ProgressModelType } from './Progress';
import type { StatusEnum } from './MasterLocation';
import type { AuditMetadataModelType } from 'common/models/flow/AuditMetadata';
import type { PjPlanConfigurationModelType } from './PjPlanConfiguration';
import type { FpSalesbeatPlanModelType } from 'common/models/flow/FpSalesbeatPlan';

import { TeamId } from './TeamId';
import { PlanId } from './PlanId';
import { OutletsInfo } from './OutletsInfo';
import { RidersInfo } from './RidersInfo';
import { PjpSummary } from './PjpSummary';
import { PjPlanStatus } from './PjPlanStatus';
import { Progress } from './Progress';
import { AuditMetadata } from 'common/models/flow/AuditMetadata';
import { PjPlanConfiguration } from 'common/models/flow/PjPlanConfiguration';

export type PlanStatusEnum =
    | 'RECEIVED'
    | 'PLANNING'
    | 'SCHEDULING'
    | 'COMPLETED'
    | 'ERROR';

export type PermanentJourneyPlanModelType = {
    clientId: string,
    id: string,
    code: string,
    name: string,
    batch: {
        clientId: string,
        batchId: string
    },
    createdOn: string,
    status: StatusEnum,
    version: number,
    teamId: TeamIdModelType,
    outletsInfo: OutletsInfoModelType,
    ridersInfo: RidersInfoModelType,
    distributionDelay: number,
    metricsReport: string,
    plan: PlanIdModelType,
    existingPlan: PlanIdModelType,
    distributionPlan: PlanIdModelType,
    pjPlanInsights: Object,
    planSummary: PjpSummaryModelType,
    customFields: { [key: string]: string },
    customProperties: { [key: string]: string },
    planStatus: PlanStatusEnum,
    statusUpdates: Array<PjPlanStatusModelType>,
    progress: Array<ProgressModelType>,
    notes?: Array<string>,
    auditMetadata: ?AuditMetadataModelType,
    homebaseId: string,
    configuration: ?PjPlanConfigurationModelType,
    salesbeatPlan: ?FpSalesbeatPlanModelType,
    isIncremental: boolean,
    basePjPlanId: string
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class PermanentJourneyPlan extends ImmutableModel {
    static fromJS(json: PermanentJourneyPlanModelType): PermanentJourneyPlan {
        const state: Object = Object.assign({}, json);
        state.teamId = TeamId.fromJS(state.teamId);
        state.outletsInfo = OutletsInfo.fromJS(state.outletsInfo);
        state.ridersInfo = RidersInfo.fromJS(state.ridersInfo);
        state.plan = PlanId.fromJS(state.plan);
        state.existingPlan = PlanId.fromJS(state.existingPlan);
        state.distributionPlan = PlanId.fromJS(state.distributionPlan);
        state.planSummary = PjpSummary.fromJS(state.planSummary);
        state.customFields = Immutable.Map(state.customFields);
        state.customProperties = Immutable.Map(state.customProperties);
        state.statusUpdates = Immutable.List(state.statusUpdates).map(item =>
            PjPlanStatus.fromJS(item)
        );
        state.progress = Immutable.List(state.progress).map(item =>
            Progress.fromJS(item)
        );
        state.notes = state.notes ? Immutable.List(state.notes) : state.notes;
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.configuration = state.configuration
            ? PjPlanConfiguration.fromJS(state.configuration)
            : state.configuration;
        state.salesbeatPlan = state.salesbeatPlan
            ? FpSalesbeatPlan.fromJS(state.salesbeatPlan)
            : state.salesbeatPlan;
        return new this(Immutable.Map(state));
    }

    toJS(): PermanentJourneyPlanModelType {
        const js: PermanentJourneyPlanModelType = {
            clientId: this.clientId,
            id: this.id,
            code: this.code,
            name: this.name,
            batch: this.batch,
            createdOn: this.createdOn,
            status: this.status,
            version: this.version,
            teamId: this.teamId.toJS(),
            outletsInfo: this.outletsInfo.toJS(),
            ridersInfo: this.ridersInfo.toJS(),
            distributionDelay: this.distributionDelay,
            metricsReport: this.metricsReport,
            plan: this.plan.toJS(),
            existingPlan: this.existingPlan.toJS(),
            distributionPlan: this.distributionPlan.toJS(),
            pjPlanInsights: this.pjPlanInsights,
            planSummary: this.planSummary.toJS(),
            customFields: this.customFields.toObject(),
            customProperties: this.customProperties.toObject(),
            planStatus: this.planStatus,
            statusUpdates: this.statusUpdates
                .toArray()
                .map(item => item.toJS()),
            progress: this.progress.toArray().map(item => item.toJS()),
            auditMetadata: this.auditMetadata
                ? this.auditMetadata.toJS()
                : this.auditMetadata,
            homebaseId: this.homebaseId,
            configuration: this.configuration
                ? this.configuration.toJS()
                : this.configuration,
            salesbeatPlan: this.salesbeatPlan
                ? this.salesbeatPlan.toJS()
                : this.salesbeatPlan,
            isIncremental: this.isIncremental,
            basePjPlanId: this.basePjPlanId
        };

        if (this.notes != null) {
            js.notes = this.notes ? this.notes.toArray() : this.notes;
        }

        return js;
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get batch(): {
        clientId: string,
        batchId: string
    } {
        return this._state.get('batch');
    }

    setBatch(batch: { clientId: string, batchId: string }): this {
        return this.clone(this._state.set('batch', batch));
    }

    get createdOn(): string {
        return this._state.get('createdOn');
    }

    setCreatedOn(createdOn: string): this {
        return this.clone(this._state.set('createdOn', createdOn));
    }

    get status(): StatusEnum {
        return this._state.get('status');
    }

    setStatus(status: StatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }

    get teamId(): TeamId {
        return this._state.get('teamId');
    }

    setTeamId(teamId: TeamId): this {
        return this.clone(this._state.set('teamId', teamId));
    }

    get outletsInfo(): OutletsInfo {
        return this._state.get('outletsInfo');
    }

    setOutletsInfo(outletsInfo: OutletsInfo): this {
        return this.clone(this._state.set('outletsInfo', outletsInfo));
    }

    get ridersInfo(): RidersInfo {
        return this._state.get('ridersInfo');
    }

    setRidersInfo(ridersInfo: RidersInfo): this {
        return this.clone(this._state.set('ridersInfo', ridersInfo));
    }

    get distributionDelay(): number {
        return this._state.get('distributionDelay');
    }

    setDistributionDelay(distributionDelay: number): this {
        return this.clone(
            this._state.set('distributionDelay', distributionDelay)
        );
    }

    get metricsReport(): string {
        return this._state.get('metricsReport');
    }

    setMetricsReport(metricsReport: string): this {
        return this.clone(this._state.set('metricsReport', metricsReport));
    }

    get plan(): PlanId {
        return this._state.get('plan');
    }

    setPlan(plan: PlanId): this {
        return this.clone(this._state.set('plan', plan));
    }

    get existingPlan(): PlanId {
        return this._state.get('existingPlan');
    }

    setExistingPlan(existingPlan: PlanId): this {
        return this.clone(this._state.set('existingPlan', existingPlan));
    }

    get distributionPlan(): PlanId {
        return this._state.get('distributionPlan');
    }

    setDistributionPlan(distributionPlan: PlanId): this {
        return this.clone(
            this._state.set('distributionPlan', distributionPlan)
        );
    }

    get pjPlanInsights(): Object {
        return this._state.get('pjPlanInsights');
    }

    setPjPlanInsights(pjPlanInsights: Object): this {
        return this.clone(this._state.set('pjPlanInsights', pjPlanInsights));
    }

    get planSummary(): PjpSummary {
        return this._state.get('planSummary');
    }

    setPlanSummary(planSummary: PjpSummary): this {
        return this.clone(this._state.set('planSummary', planSummary));
    }

    get customFields(): Immutable.Map<string, string> {
        return this._state.get('customFields');
    }

    setCustomFields(customFields: Immutable.Map<string, string>): this {
        return this.clone(this._state.set('customFields', customFields));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get planStatus(): PlanStatusEnum {
        return this._state.get('planStatus');
    }

    setPlanStatus(planStatus: PlanStatusEnum): this {
        return this.clone(this._state.set('planStatus', planStatus));
    }

    get statusUpdates(): Immutable.List<PjPlanStatus> {
        return this._state.get('statusUpdates');
    }

    setStatusUpdates(statusUpdates: Immutable.List<PjPlanStatus>): this {
        return this.clone(this._state.set('statusUpdates', statusUpdates));
    }

    get progress(): Immutable.List<Progress> {
        return this._state.get('progress');
    }

    setProgress(progress: Immutable.List<Progress>): this {
        return this.clone(this._state.set('progress', progress));
    }

    get notes(): ?Immutable.List<string> {
        return this._state.get('notes');
    }

    setNotes(notes: ?Immutable.List<string>): this {
        return this.clone(this._state.set('notes', notes));
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: ?AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get homebaseId(): string {
        return this._state.get('homebaseId');
    }

    setHomebaseId(homebaseId: string): this {
        return this.clone(this._state.set('homebaseId', homebaseId));
    }

    get configuration(): ?PjPlanConfiguration {
        return this._state.get('configuration');
    }

    setConfiguration(configuration: ?PjPlanConfiguration): this {
        return this.clone(this._state.set('configuration', configuration));
    }

    get salesbeatPlan(): ?FpSalesbeatPlan {
        return this._state.get('salesbeatPlan');
    }

    setSalesbeatPlan(salesbeatPlan: ?FpSalesbeatPlan): this {
        return this.clone(this._state.set('salesbeatPlan', salesbeatPlan));
    }

    get isIncremental(): boolean {
        return this._state.get('isIncremental');
    }

    setIsIncremental(isIncremental: boolean): this {
        return this.clone(this._state.set('isIncremental', isIncremental));
    }

    get basePjPlanId(): string {
        return this._state.get('basePjPlanId');
    }

    setBasePjPlanId(basePjPlanId: string): this {
        return this.clone(this._state.set('basePjPlanId', basePjPlanId));
    }
}
